import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';
import { worstWanUptime } from '../lib/wanUptime';

/**
 * UniFi Site Manager is the live network source today. Rows come from
 * unifi_sites_customer_view (counts only — no ids or keys) and refresh
 * every ~5 minutes. A location may map to several UniFi sites (extra
 * networks), so aggregate by summing.
 */
export interface UnifiSiteSummary {
  id: string;
  customer_id: string;
  location_id: string | null;
  site_name: string | null;
  unifi_host_name: string | null;
  wifi_client_count: number | null;
  wired_client_count: number | null;
  ssid_count: number | null;
  total_devices: number | null;
  offline_devices: number | null;
  wifi_devices: number | null;
  wired_devices: number | null;
  gateway_devices: number | null;
  offline_gateway_devices: number | null;
  guest_clients: number | null;
  pending_update_devices: number | null;
  critical_notifications: number | null;
  wan_links: number | null;
  wan_uptime_pct: number | null;
  timezone: string | null;
  updated_at: string | null;
}

const UNIFI_SITE_COLUMNS =
  'id, customer_id, location_id, site_name, unifi_host_name, wifi_client_count, wired_client_count, ssid_count, total_devices, offline_devices, wifi_devices, wired_devices, gateway_devices, offline_gateway_devices, guest_clients, pending_update_devices, critical_notifications, wan_links, wan_uptime_pct, timezone, updated_at';

export const UNIFI_EXPECTED_REFRESH_MS = 5 * 60 * 1000;
export const UNIFI_STALE_MS = 30 * 60 * 1000;

export function useUnifiSites(locationId?: string) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['unifi-sites', scope.key, locationId ?? ''],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<UnifiSiteSummary[]> => {
      let query = supabase
        .from('unifi_sites_customer_view')
        .select(UNIFI_SITE_COLUMNS)
        .in('customer_id', scope.ids)
        .order('site_name');
      if (locationId) query = query.eq('location_id', locationId);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as UnifiSiteSummary[];
    },
    staleTime: 60 * 1000,
  });
}

export interface UnifiAggregate {
  siteCount: number;
  totalDevices: number | null;
  offlineDevices: number | null;
  onlineDevices: number | null;
  gatewayDevices: number | null;
  offlineGatewayDevices: number | null;
  /** Worst WAN uptime across the location's circuits, 0–100. Null until UniFi reports it. */
  wanUptimePct: number | null;
  wifiClients: number | null;
  wiredClients: number | null;
  ssids: number | null;
  pendingUpdates: number | null;
  criticalNotifications: number | null;
  latestUpdatedAt: string | null;
}

function sumNullable(values: Array<number | null | undefined>): number | null {
  let sum = 0;
  let any = false;
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      sum += v;
      any = true;
    }
  }
  return any ? sum : null;
}

/** Sums counts across sites; every field is null when no site reported it. */
export function aggregateUnifi(sites: UnifiSiteSummary[]): UnifiAggregate {
  const totalDevices = sumNullable(sites.map((s) => s.total_devices));
  const offlineDevices = sumNullable(sites.map((s) => s.offline_devices));
  return {
    siteCount: sites.length,
    totalDevices,
    offlineDevices,
    onlineDevices:
      totalDevices !== null ? Math.max(totalDevices - (offlineDevices ?? 0), 0) : null,
    gatewayDevices: sumNullable(sites.map((s) => s.gateway_devices)),
    offlineGatewayDevices: sumNullable(sites.map((s) => s.offline_gateway_devices)),
    wanUptimePct: worstWanUptime(sites.map((s) => s.wan_uptime_pct)),
    wifiClients: sumNullable(sites.map((s) => s.wifi_client_count)),
    wiredClients: sumNullable(sites.map((s) => s.wired_client_count)),
    ssids: sumNullable(sites.map((s) => s.ssid_count)),
    pendingUpdates: sumNullable(sites.map((s) => s.pending_update_devices)),
    criticalNotifications: sumNullable(sites.map((s) => s.critical_notifications)),
    latestUpdatedAt: sites.reduce<string | null>(
      (max, s) => (s.updated_at && (!max || s.updated_at > max) ? s.updated_at : max),
      null,
    ),
  };
}

/** Groups sites by location for per-site cards / list columns. */
export function unifiByLocation(sites: UnifiSiteSummary[]): Map<string, UnifiAggregate> {
  const grouped = new Map<string, UnifiSiteSummary[]>();
  for (const site of sites) {
    if (!site.location_id) continue;
    const list = grouped.get(site.location_id) ?? [];
    list.push(site);
    grouped.set(site.location_id, list);
  }
  const out = new Map<string, UnifiAggregate>();
  for (const [locationId, list] of grouped) out.set(locationId, aggregateUnifi(list));
  return out;
}
